import Cookies from 'js-cookie';
import {
    BOTBLITZ_DTRADER_SYNC_COOKIE_NAME,
    BOTBLITZ_SYNC_APP_ID_KEY,
    BOTBLITZ_SYNC_OAUTH_CLIENT_ID_KEY,
    BOTBLITZ_SYNC_OAUTH_DERIV_APP_ID_KEY,
} from '../../constants/pu-sync-storage';

/*
 * Configuration values needed in js codes
 *
 * NOTE:
 * Please use the following command to avoid accidentally committing personal changes
 * git update-index --assume-unchanged packages/shared/src/utils/config.js
 *
 */

export const livechat_license_id = 12049137;
export const livechat_client_id = '66aa088aad5a414484c1fd1fa8a5ace7';

export const domain_app_ids = {
    // these domains as supported "production domains"
    'deriv.app': 16929, // TODO: [app-link-refactor] - Remove backwards compatibility for `deriv.app`
    'app.deriv.com': 16929,
    'staging-app.deriv.com': 16303,
    'app.deriv.me': 1411,
    'staging-app.deriv.me': 1411, // TODO: setup staging for deriv.me
    'app.deriv.be': 30767,
    'staging-app.deriv.be': 31186,
    'binary.com': 1,
    'test-app.deriv.com': 51072,
};

export const platform_app_ids = {
    derivgo: 23789,
};

export const getCurrentProductionDomain = () =>
    !/^staging\./.test(window.location.hostname) &&
    Object.keys(domain_app_ids).find(domain => window.location.hostname === domain);

export const isProduction = () => {
    const all_domains = Object.keys(domain_app_ids).map(domain => `(www\\.)?${domain.replace('.', '\\.')}`);
    return new RegExp(`^(${all_domains.join('|')})$`, 'i').test(window.location.hostname);
};

export const isTestLink = () => {
    return /^((.*)\.binary\.sx)$/i.test(window.location.hostname);
};

export const isLocal = () => /localhost(:\d+)?$/i.test(window.location.hostname);

const STORAGE_KEY = 'config.app_id';

/**
 * Sync cookie **`v: 2`** = format revision only (alias **`v: 3`** still parsed). **`auth_flow`** = legacy vs oauth2 API.
 */
export type TBotblitzDtraderSyncPayload =
    | {
          v: 2;
          auth_flow: 'legacy';
          active_loginid: string;
          app_id: string;
          ts: number;
      }
    | {
          v: 2;
          auth_flow: 'oauth2';
          active_loginid: string;
          oauth_client_id: string;
          deriv_app_id: string;
          ts: number;
      };

function parsePuDtraderSyncCookieRaw(raw: string): TBotblitzDtraderSyncPayload | null {
    try {
        const o = JSON.parse(raw) as Record<string, unknown>;
        if (!o) return null;

        const rev = o.v;
        if (rev !== 2 && rev !== 3) return null;

        if (typeof o.active_loginid !== 'string' || !o.active_loginid) return null;
        if (o.auth_flow !== 'legacy' && o.auth_flow !== 'oauth2') return null;
        const ts = typeof o.ts === 'number' ? o.ts : Date.now();

        if (o.auth_flow === 'legacy') {
            const app_id = typeof o.app_id === 'string' ? o.app_id.trim() : '';
            if (!/^\d+$/.test(app_id)) return null;
            if (typeof o.oauth_client_id === 'string' && o.oauth_client_id.trim() !== '') return null;
            if (typeof o.deriv_app_id === 'string' && o.deriv_app_id.trim() !== '') return null;
            return { v: 2, auth_flow: 'legacy', active_loginid: o.active_loginid, app_id, ts };
        }

        const oauth_client_id =
            typeof o.oauth_client_id === 'string' ? o.oauth_client_id.trim() : '';
        if (!oauth_client_id) return null;

        const fromOldDeriv =
            typeof o.oauth_deriv_app_id === 'string' && /^\d+$/.test(o.oauth_deriv_app_id.trim())
                ? o.oauth_deriv_app_id.trim()
                : '';
        const fromDerivField =
            typeof o.deriv_app_id === 'string' && /^\d+$/.test(o.deriv_app_id.trim())
                ? o.deriv_app_id.trim()
                : '';
        const fromLegacyAppIdSlot =
            typeof o.app_id === 'string' && /^\d+$/.test(o.app_id.trim()) ? o.app_id.trim() : '';
        const deriv_app_id = fromOldDeriv || fromDerivField || fromLegacyAppIdSlot;
        if (!/^\d+$/.test(deriv_app_id)) return null;

        return {
            v: 2,
            auth_flow: 'oauth2',
            active_loginid: o.active_loginid,
            oauth_client_id,
            deriv_app_id,
            ts,
        };
    } catch {
        return null;
    }
}

/**
 * True when dtrader-c runs **inside a parent frame** (PU embed). The PU sets cookies with
 * `Domain=.<registrable>` from the **parent** host (root `getParentDomainForSharedCookies`: last two labels, direct TLD only).
 * No hostname allowlist — the browser attaches the right cookies for the iframe host automatically.
 */
export const isEmbeddedPuIframe = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.self !== 'undefined' &&
    typeof window.top !== 'undefined' &&
    window.self !== window.top;

/** @deprecated Use `isEmbeddedPuIframe`; name kept for older imports. */
export const isEverymanDeploymentHost = isEmbeddedPuIframe;

/**
 * Read PU root sync cookie (`botblitz_dtrader_sync`). Legacy flow sets numeric **APP_ID**; oauth2 sets
 * **OAUTH_CLIENT_ID** + **Deriv-App-ID** and clears the opposite flow’s keys.
 */
export const applyEverymanSyncCookieToLocalStorage = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const raw = Cookies.get(BOTBLITZ_DTRADER_SYNC_COOKIE_NAME);
        if (!raw) return false;
        const parsed = parsePuDtraderSyncCookieRaw(raw);
        if (!parsed) return false;

        if (parsed.auth_flow === 'legacy') {
            localStorage.setItem(STORAGE_KEY, parsed.app_id);
            localStorage.setItem(BOTBLITZ_SYNC_APP_ID_KEY, parsed.app_id);
            localStorage.removeItem(BOTBLITZ_SYNC_OAUTH_DERIV_APP_ID_KEY);
            localStorage.removeItem(BOTBLITZ_SYNC_OAUTH_CLIENT_ID_KEY);
            // eslint-disable-next-line no-console
            console.info('🖼️ [botblitz-dtrader-sync] cookie_applied_to_ls', {
                source: 'root_sync_cookie',
                mode: 'legacy_app_id',
                auth_flow: 'legacy',
                app_id_len: parsed.app_id.length,
                active_loginid_len: parsed.active_loginid.length,
            });
            return true;
        }

        localStorage.setItem(BOTBLITZ_SYNC_OAUTH_DERIV_APP_ID_KEY, parsed.deriv_app_id);
        localStorage.setItem(BOTBLITZ_SYNC_OAUTH_CLIENT_ID_KEY, parsed.oauth_client_id);
        localStorage.setItem(STORAGE_KEY, parsed.deriv_app_id);
        localStorage.setItem(BOTBLITZ_SYNC_APP_ID_KEY, parsed.deriv_app_id);

        // eslint-disable-next-line no-console
        console.info('🖼️ [botblitz-dtrader-sync] cookie_applied_to_ls', {
            source: 'root_sync_cookie',
            mode: 'oauth2_client',
            auth_flow: 'oauth2',
            deriv_app_id_len: parsed.deriv_app_id.length,
            oauth_client_id_len: parsed.oauth_client_id.length,
            active_loginid_len: parsed.active_loginid.length,
        });
        return true;
    } catch {
        return false;
    }
};

const DEV_APP_ID = 113222;

/** Local / IP dev shell default; embedded apps get `app_id` from sync cookie, `?app_id=`, or bridge. */
const getAppIdForDomain = (): string => {
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname.toLowerCase();
    if (isLocal() || /^[\d.]+$/.test(host) || host.endsWith('.localhost')) {
        return String(DEV_APP_ID);
    }
    return '';
};

/**
 * Resolve numeric WS `app_id`. In a PU iframe prefer LS from parent sync cookie / bridge, then `?app_id=`.
 */
const getAppIdFromUrlOrConstant = (): string => {
    if (typeof window === 'undefined') return getAppIdForDomain();

    if (isEmbeddedPuIframe()) {
        try {
            const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
            if (stored && /^\d+$/.test(stored)) return stored;
        } catch {
            /* ignore */
        }
        try {
            const params = new URLSearchParams(window.location.search);
            const fromUrl = params.get('app_id');
            if (fromUrl && /^\d+$/.test(fromUrl)) return fromUrl;
        } catch {
            /* ignore */
        }
        // eslint-disable-next-line no-console
        console.error(
            '[embed-sync] Missing valid config.app_id. PU should set parent Domain cookies (see root getParentDomainForSharedCookies) or pass ?app_id= on the iframe URL.'
        );
        return '';
    }

    try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('app_id');
        if (fromUrl && /^\d+$/.test(fromUrl)) return fromUrl;
    } catch {
        // ignore
    }
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored && /^\d+$/.test(stored)) return stored;
    } catch {
        // ignore
    }
    return getAppIdForDomain();
};

/**
 * Resolve app_id (from URL if present, else APP_ID) and store in localStorage.
 * Only updates localStorage when the value is missing or different.
 * Call once at app bootstrap (lowest level); rest of the app reads from localStorage via getAppId().
 */
export const syncAppIdFromDomain = (): void => {
    if (typeof window === 'undefined') return;
    applyEverymanSyncCookieToLocalStorage();
    const resolved = getAppIdFromUrlOrConstant();
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored !== resolved) {
            localStorage.setItem(STORAGE_KEY, resolved);
        }
    } catch {
        // ignore
    }
};

/**
 * Returns app_id from localStorage (set once on page load by syncAppIdFromDomain).
 * If storage is empty (e.g. before sync ran), runs sync once then returns.
 * @deprecated Please use 'WebSocketUtils.getAppId' from '@deriv-com/utils' instead of this.
 */
export const getAppId = (): string => {
    if (typeof window === 'undefined') return getAppIdForDomain();
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored) return stored;
    } catch {
        // ignore
    }
    syncAppIdFromDomain();
    const resolved = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
    if (resolved) return resolved;
    if (isEmbeddedPuIframe()) return '';
    return getAppIdForDomain();
};

/**
 * V2 REST + OTP: `Deriv-App-ID` header (must match OAuth registration). From sync LS after V2 cookie; falls back to
 * `getAppId()` only when no V2 mirror key (e.g. older builds).
 */
export const getOAuthDerivAppIdFromEverymanSync = (): string => {
    if (typeof window === 'undefined') return '';
    try {
        const fromCookie = localStorage.getItem(BOTBLITZ_SYNC_OAUTH_DERIV_APP_ID_KEY)?.trim();
        if (fromCookie && /^\d+$/.test(fromCookie)) return fromCookie;
    } catch {
        /* ignore */
    }
    return getAppId();
};

/** True when parent sync cookie applied V2 identifiers (PKCE client + Deriv-App-ID), not V1 `APP_ID` alone. */
export const isEverymanSyncV2OAuthClientMode = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const id = localStorage.getItem(BOTBLITZ_SYNC_OAUTH_CLIENT_ID_KEY)?.trim();
        return !!id;
    } catch {
        return false;
    }
};

/**
 * Returns the Deriv Bot base URL for the current app.
 */
export const getBotBaseUrl = (): string => {
    return 'https://bbot.botblitz.site';
};

// Same as root: single production WebSocket URL (ws.derivws.com), optional localStorage override
export const getSocketURL = (_is_wallets = false) => {
    const local_storage_server_url = window.localStorage.getItem('config.server_url');
    if (local_storage_server_url) return local_storage_server_url;
    return 'ws.derivws.com';
};

export const checkAndSetEndpointFromUrl = () => {
    if (isTestLink()) {
        const url_params = new URLSearchParams(location.search.slice(1));

        if (url_params.has('qa_server')) {
            const qa_server = url_params.get('qa_server') || '';

            url_params.delete('qa_server');

            if (/^(^(www\.)?qa[0-9]{1,4}\.deriv.dev|(.*)\.derivws\.com)$/.test(qa_server)) {
                localStorage.setItem('config.server_url', qa_server);
            }

            const params = url_params.toString();
            const hash = location.hash;

            location.href = `${location.protocol}//${location.hostname}${location.pathname}${
                params ? `?${params}` : ''
            }${hash || ''}`;

            return true;
        }
    }

    return false;
};

export const getDebugServiceWorker = () => {
    const debug_service_worker_flag = window.localStorage.getItem('debug_service_worker');
    if (debug_service_worker_flag) return !!parseInt(debug_service_worker_flag);

    return false;
};
